import * as _ from 'lodash';
import React, { useState } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
} from 'recharts';

import { Util } from '@finley-technologies/finley-types';

import { CoreBorrowingBaseInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  ChartTimePeriod,
  currencyFormatter,
  dateFormatter,
  tooltipFormatter,
} from '../shared/portfolioChartUtils';
import { colors, transparency } from 'packages/common-frontend/colors';

type PortfolioSummaryChartProps = {
  chartData: CoreBorrowingBaseInsightsGqlDataGQLType[];
  selectedTimePeriod: ChartTimePeriod;
  currentSelectedBarReportingDate: Util.FullDateStr;
  onSelectBar: (
    selectedBarData: CoreBorrowingBaseInsightsGqlDataGQLType,
  ) => void;
};
export const PortfolioSummaryChart = ({
  chartData,
  currentSelectedBarReportingDate,
  onSelectBar,
  selectedTimePeriod,
}: PortfolioSummaryChartProps) => {
  const [mouseHoverBarIndex, setMouseHoverBarIndex] = useState<number | null>(
    null,
  );
  return (
    <ResponsiveContainer width="100%" height={600}>
      <ComposedChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 30, bottom: 0 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="reportingDate"
          height={70}
          tick={{ fontWeight: 600 }}
          tickFormatter={(value: any) =>
            dateFormatter({ date: value, timePeriod: selectedTimePeriod })
          }
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={currencyFormatter}
          tick={{ fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
          tickCount={7}
        />
        <Tooltip
          formatter={(value: number, name: string) =>
            tooltipFormatter(value, name, 'currency')
          }
          itemStyle={{ color: colors.gray[500] }}
          cursor={{ stroke: 'none' }}
          contentStyle={{ fontSize: '12px' }}
        />
        <Legend
          formatter={(value) => (
            <span
              style={{
                color: 'black',
                fontWeight: 500,
                verticalAlign: 'text-top',
                margin: '0 40px 0 16px',
              }}
            >
              {_.startCase(value)}
            </span>
          )}
          iconSize={20}
          align="left"
          wrapperStyle={{ padding: '16px 24px' }}
        />
        <Bar
          cursor="pointer"
          onClick={(data) => {
            onSelectBar(data.payload);
          }}
          dataKey="currentPrincipalBalance"
          fill={`${colors.chart.lilac}${transparency[50]}`}
          barSize={64}
        >
          {chartData.map((entry, index) => {
            // bar is consider active if it is the selected bar or being hovered over
            const isBarActive =
              entry.reportingDate === currentSelectedBarReportingDate ||
              index === mouseHoverBarIndex;
            return (
              <Cell
                cursor="pointer"
                fill={
                  isBarActive
                    ? colors.chart.lilac
                    : `${colors.chart.lilac}${transparency[50]}`
                }
                key={`cell-${entry.reportingDate}=${entry.fileVersion}`}
                onMouseEnter={() => setMouseHoverBarIndex(index)}
                onMouseLeave={() => setMouseHoverBarIndex(null)}
              />
            );
          })}
        </Bar>
        <Line
          dataKey="totalAssets"
          fill={colors.chart.mint}
          stroke={colors.chart.mint}
          strokeWidth="2px"
          dot={false}
        />
        <Line
          dataKey="totalBorrowingBaseAmount"
          fill={colors.chart.cream}
          stroke={colors.chart.cream}
          strokeWidth="2px"
          dot={false}
        />
        <Line
          dataKey="commitmentAmount"
          fill={colors.chart.blush}
          stroke={colors.chart.blush}
          strokeWidth="2px"
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
